import React from 'react';

/**
 * The rendered width of an element, kept current as it resizes.
 *
 * For drawings that need real pixel sizes — an SVG stretched with
 * `preserveAspectRatio="none"` squashes its dots into ovals — but should
 * still fill whatever box they are placed in. Attach the returned ref to the
 * element to measure; the width is 0 until it has been laid out.
 */
export function useElementWidth<T extends HTMLElement = HTMLDivElement>(): [(node: T | null) => void, number] {
  const [node, setNode] = React.useState<T | null>(null);
  const [width, setWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    if (!node) return undefined;
    setWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setNode, width];
}
